import React from "react";
import { Languages, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card";
import { SettingsBanner } from "../../components/ui/SettingsBanner";
import { ToggleSwitch } from "../../components/ui/ToggleSwitch";
import { useToggleableSetting } from "../../hooks/useToggleableSetting";
import { settingsApi } from "../../utils/api";
import { languageName } from "../../utils/helpers";

// The per-language kill-switch for translated event and venue prose (#688): one toggle per locale
// the API can serve (`available_locales`: the UI locales but English). A save sends the
// whole `enabled_locales` list, so two toggles flipped in a row cannot drop each other.
export default function LocalizedContent() {
  const setting = useToggleableSetting(
    settingsApi.getLocalizedContentSettings,
    settingsApi.updateLocalizedContentSettings,
    {
      loadErrorMessage: "Failed to load localized content settings",
      updateErrorMessage: "Failed to update localized content settings",
      successMessage: "Localized content settings updated",
    }
  );
  const enabled = setting.value?.enabled_locales ?? [];
  const available = setting.value?.available_locales ?? [];

  const toggle = (code) =>
    setting.save({
      enabled_locales: enabled.includes(code) ? enabled.filter((c) => c !== code) : [...enabled, code],
    });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Languages className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Localized content</CardTitle>
        </div>
        <CardDescription>
          Per language, whether readers are served machine-translated event descriptions, card summaries and
          venue details.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SettingsBanner message={setting.message} error={setting.error} />
        {setting.loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />Loading...
          </div>
        ) : setting.value ? (
          <div className="space-y-4">
            {available.length === 0 && (
              <p className="text-sm text-muted-foreground">No translation languages are configured.</p>
            )}
            {available.map((code) => (
              <ToggleSwitch
                key={code}
                label={`${languageName(code)} (${code})`}
                description="Serve machine-translated descriptions, card summaries and venue details to readers in this language. Untranslated text stays in its source language. Takes effect within about a minute."
                checked={enabled.includes(code)}
                disabled={setting.saving}
                onToggle={() => toggle(code)}
              />
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
